import { h, render } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { fetchCatalogs } from "../global/storefront/catalogs";
import { useAsync } from "../global/hooks/useAsync";
import { ArrowLeftIcon, ArrowRightIcon } from "../global/icons/icons";
import { CatalogCard, SkeletonCard } from "../global/catalog-card/catalog-card";

const CARD_SIZES = "(min-width: 1024px) 22vw, (min-width: 768px) 30vw, 45vw";
const CARD_IMAGE_WIDTH = 400;
const SKELETON_COUNT = 5;

function CarouselSwiper({ catalogs }) {
  const swiperRef = useRef(null);

  useEffect(() => {
    if (!swiperRef.current || !window.loadSwiper) return;

    let swiperInstance;
    let cancelled = false;

    window.loadSwiper.then(({ Swiper, Navigation }) => {
      if (cancelled || !swiperRef.current) return;
      swiperInstance = new Swiper(swiperRef.current, {
        modules: [Navigation],
        slidesPerView: "auto",
        spaceBetween: 8,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
        breakpoints: {
          768: {
            slidesOffsetBefore: 40,
            slidesOffsetAfter: 40,
          },
        },
        navigation: {
          nextEl: swiperRef.current.querySelector("[data-nav-next]"),
          prevEl: swiperRef.current.querySelector("[data-nav-prev]"),
        },
      });
    });

    return () => {
      cancelled = true;
      swiperInstance?.destroy(true, true);
    };
  }, [catalogs]);

  return (
    <div ref={swiperRef} class="carousel related-catalogs__carousel swiper">
      <div class="carousel__items related-catalogs__items swiper-wrapper">
        {catalogs.map((catalog) => (
          <div class="related-catalogs__slide swiper-slide" key={catalog.id}>
            <CatalogCard
              catalog={catalog}
              sizes={CARD_SIZES}
              imageWidth={CARD_IMAGE_WIDTH}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        class="carousel__nav-button carousel__nav-button--prev related-catalogs__carousel-nav-button related-catalogs__carousel-nav-button--prev custom-button button--white"
        data-nav-prev
        aria-label="Previous slide"
      >
        <ArrowLeftIcon />
      </button>
      <button
        type="button"
        class="carousel__nav-button carousel__nav-button--next related-catalogs__carousel-nav-button related-catalogs__carousel-nav-button--next custom-button button--white"
        data-nav-next
        aria-label="Next slide"
      >
        <ArrowRightIcon />
      </button>
    </div>
  );
}

function SkeletonCarousel() {
  return (
    <div class="related-catalogs__carousel related-catalogs__carousel--skeleton" aria-hidden="true">
      <div class="related-catalogs__items related-catalogs__items--skeleton">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <div class="related-catalogs__slide" key={i}>
            <SkeletonCard />
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatedCatalogsList({ currentHandle, limit }) {
  const { status, data } = useAsync(fetchCatalogs);

  if (status === "loading") return <SkeletonCarousel />;
  if (status === "error") return null;

  const catalogs = (data || [])
    .filter((catalog) => catalog.title && catalog.date)
    .filter((catalog) => catalog.handle !== currentHandle)
    .slice(0, limit);

  if (!catalogs.length) return null;

  return <CarouselSwiper catalogs={catalogs} />;
}

class RelatedCatalogs extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector("[data-container]");
    const currentHandle = this.dataset.currentHandle || "";
    const limit = parseInt(this.dataset.limit, 10) || 24;
    render(
      <RelatedCatalogsList currentHandle={currentHandle} limit={limit} />,
      this.container,
    );
  }

  disconnectedCallback() {
    render(null, this.container);
  }
}

if (!customElements.get("related-catalogs")) {
  customElements.define("related-catalogs", RelatedCatalogs);
}
